/** W82 Design-Web-1 (D-1661, Teil B c): Fristen-Zeilen rücken nach, statt zu springen (Layout animation, FLIP), nach
 *  `9c93e244:web/src/neu/flip.ts` (Entwurf), nur für Fristen-Zeilen (`li[data-frist-zeile]`, Fristen-Seite und Fristen-Modul der
 *  Startseite), nie für Aufgaben (DWW-4). Ein Druck auf „Abhaken“ merkt sich die Lage der Fristen-Zeilen; ändert sich danach die
 *  Liste, gleitet jede bewegte Zeile mit der Feder „ordnen“ an ihre neue Stelle, eine entfernte geht als Abbild hinaus (hebt
 *  sich, gleitet 24 px zur Seite, blendet aus), eine neue blendet ein. Nur transform und opacity, Abbilder ohne Zeiger und für
 *  Vorlesen verborgen. Reduzierte Bewegung und `?still`: nichts; ruhige Fassung: ohne Überschwingen. Beobachtet höchstens 3,5 s. */
import { bewegungReduziert, feder, federBilder, ruhigeFassung, stillGeschaltet } from "./feder";

const ZEILE = "li[data-frist-zeile]";
type Lage = { x: number; y: number; w: number; h: number; liste: Element | null };

function lagenMessen(wurzel: Element): Map<string, Lage> {
  const m = new Map<string, Lage>();
  for (const el of wurzel.querySelectorAll(ZEILE)) {
    const k = el.getAttribute("data-frist-zeile");
    if (!k || m.has(k)) continue;
    const r = el.getBoundingClientRect();
    if (r.width && r.height) m.set(k, { x: r.left, y: r.top, w: r.width, h: r.height, liste: el.parentElement });
  }
  return m;
}

type Lauf = { wurzel: Element; beob: MutationObserver; uhr: ReturnType<typeof setTimeout>; getippt: Set<string>; abbilder: Map<string, HTMLElement>; ende: () => void };
let laufend: Lauf | null = null;

/** Abbilder der Zeilen, die verschwinden könnten (die gedrückte und ihre Nachbarn in derselben Liste) */
function abbilderMerken(zeile: Element, abbilder: Map<string, HTMLElement>): void {
  for (const el of zeile.parentElement?.querySelectorAll(`:scope > ${ZEILE}`) ?? []) {
    const k = el.getAttribute("data-frist-zeile");
    if (k) abbilder.set(k, el.cloneNode(true) as HTMLElement);
  }
}

/** Vor dem Abhaken aufrufen (Druck auf den Kreis einer Fristen-Zeile). */
export function nachrueckenVormerken(ausloeser: Element): void {
  if (stillGeschaltet() || bewegungReduziert()) return;
  const zeile = ausloeser.closest(ZEILE);
  const wurzel = ausloeser.closest("main");
  if (!zeile || !wurzel) return;
  // die getippte Zeile: geht sie (Modul) oder wechselt sie die Gruppe (Seite, nach „Erledigt“), wird ihr Knopf ersetzt und die
  // Welle am neuen Knopf liefe nie; darum trägt ihr Abbild den abgehakten Zustand, die Welle spielt an der alten Stelle
  const getippt = zeile.getAttribute("data-frist-zeile");
  // W82 Web-11b W11B-3 (R2 Design-Web-1 F-9): ein zweites Abhaken, solange der Beobachter des ersten läuft (zwei Zeilen unter
  // 100 ms), hängt sich an ihn: die Lage von vorher, die Abbilder und jede getippte Zeile bleiben, so bekommt jede ihre Welle
  if (laufend?.wurzel === wurzel) {
    if (getippt) laufend.getippt.add(getippt);
    abbilderMerken(zeile, laufend.abbilder);
    clearTimeout(laufend.uhr);
    laufend.uhr = setTimeout(laufend.ende, 3500);
    return;
  }
  if (laufend) {
    laufend.beob.disconnect();
    clearTimeout(laufend.uhr);
  }
  let vorher = lagenMessen(wurzel);
  const getippte = new Set(getippt ? [getippt] : []);
  const abbilder = new Map<string, HTMLElement>();
  abbilderMerken(zeile, abbilder);
  const ruhe = ruhigeFassung() ? feder(1, 660 * 1.15) : feder(0.85, 660);
  let takt = 0;
  const zeileVon = (k: string) => Array.from(wurzel.querySelectorAll<HTMLElement>(ZEILE)).find((e) => e.getAttribute("data-frist-zeile") === k);
  const spielen = () => {
    takt = 0;
    const nachher = lagenMessen(wurzel);
    for (const [k, alt] of vorher) {
      const neu = nachher.get(k);
      if (neu) {
        const dx = alt.x - neu.x;
        const dy = alt.y - neu.y;
        if (Math.abs(dx) < 1 && Math.abs(dy) < 1) continue;
        const el = zeileVon(k);
        if (!el) continue;
        if (alt.liste && neu.liste && alt.liste !== neu.liste) {
          // über Gruppen hinweg (in „Erledigt“): die Zeile blendet an ihrer neuen Stelle ein, das Abbild geht hinaus
          const abbild = abbilder.get(k);
          if (abbild) hinaus(abbild, alt, getippte.has(k));
          el.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 320, delay: 160, easing: "cubic-bezier(0.16, 1, 0.3, 1)", fill: "backwards" });
          continue;
        }
        el.animate(
          federBilder(ruhe, (p) => ({ transform: `translate(${(dx * (1 - p)).toFixed(2)}px, ${(dy * (1 - p)).toFixed(2)}px)` })),
          { duration: ruhe.dauer, easing: "linear" },
        );
      } else {
        const abbild = abbilder.get(k);
        if (abbild) hinaus(abbild, alt, getippte.has(k));
      }
    }
    for (const [k] of nachher) {
      if (vorher.has(k)) continue;
      zeileVon(k)?.animate([{ opacity: 0, transform: "translateY(6px)" }, { opacity: 1, transform: "none" }], { duration: 320, easing: "cubic-bezier(0.16, 1, 0.3, 1)", delay: 120, fill: "backwards" });
    }
    vorher = nachher;
  };
  const beob = new MutationObserver(() => {
    if (!takt) takt = requestAnimationFrame(spielen);
  });
  beob.observe(wurzel, { childList: true, subtree: true });
  const ende = () => {
    beob.disconnect();
    cancelAnimationFrame(takt);
    if (laufend?.beob === beob) laufend = null;
  };
  laufend = { wurzel, beob, uhr: setTimeout(ende, 3500), getippt: getippte, abbilder, ende };
}

/** Das Abbild einer gegangenen Zeile geht hinaus: hebt sich, gleitet zur Seite, blendet ganz aus. Die getippte Zeile geht
 *  abgehakt (Kreis gefüllt, Welle aus buehne.css über `[data-getippt][aria-pressed="true"]`). */
function hinaus(abbild: HTMLElement, lage: Lage, abgehakt = false): void {
  if (abgehakt) abbild.querySelector(".abhaken[data-getippt]")?.setAttribute("aria-pressed", "true");
  const ebene = document.createElement("ul");
  ebene.className = "bue-abbild gruppe";
  ebene.setAttribute("aria-hidden", "true");
  ebene.inert = true;
  ebene.style.left = `${lage.x}px`;
  ebene.style.top = `${lage.y}px`;
  ebene.style.width = `${lage.w}px`;
  ebene.style.height = `${lage.h}px`;
  ebene.append(abbild);
  document.body.append(ebene);
  const a = ebene.animate(
    [
      { opacity: 1, transform: "none" },
      { opacity: 1, transform: "translateY(-3px) scale(1.01)", offset: 0.25 },
      { opacity: 0, transform: "translate(24px, -2px) scale(0.98)" },
    ],
    { duration: 420, easing: "cubic-bezier(0.23, 1, 0.32, 1)", fill: "forwards" },
  );
  a.onfinish = () => ebene.remove();
  a.oncancel = () => ebene.remove();
}
